import { Virtuoso } from "react-virtuoso";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import decryptUrl from "../../util/img-decryption";
import {
  addParamsURL,
  downloadFile,
  getFileNameFromDisposition,
} from "../../util/helper";
import Loader from "../UI/Loader/Loader";
import { Box, Checkbox } from "@mui/material";
import { Icon, Notifications } from "@jds/core";
import { useParams } from "react-router-dom";
import useApiHttp from "../../hooks/use-http";

import brokenImg from "../../assets/png/brokenImg.png";

import classes from "./JustifiedVirtuosoGallery.module.css";
import { useDispatch } from "react-redux";
import {
  openFileSideBar,
  setFileDetails,
} from "../../store/state-slices/fileDetailsSidebar";
/* ===============================
   HELPER: BUILD JUSTIFIED ROWS
   =============================== */
function buildJustifiedRows(images, containerWidth, rowHeight = 220, gap = 2) {
  const rows = [];
  let row = [];
  let rowAspectRatioSum = 0;

  images.forEach((img, listIndex) => {
    const aspectRatio =
      img?.metaInfo?.width / img?.metaInfo?.height || 200 / 200;
    row.push({ ...img, aspectRatio, listIndex });
    rowAspectRatioSum += aspectRatio;

    const rowWidth = rowAspectRatioSum * rowHeight + gap * (row.length - 1);

    if (rowWidth >= containerWidth) {
      const scale =
        (containerWidth - gap * (row.length - 1)) / rowAspectRatioSum;

      rows.push(
        row.map((item) => ({
          ...item,
          displayWidth: scale * item.aspectRatio,
          displayHeight: scale,
        })),
      );

      row = [];
      rowAspectRatioSum = 0;
    }
  });

  // Last row (not stretched)
  if (row.length) {
    rows.push(
      row.map((item) => ({
        ...item,
        displayWidth: rowHeight * item.aspectRatio,
        displayHeight: rowHeight,
      })),
    );
  }

  return rows;
}

/* ===============================
   ROW COMPONENT
   =============================== */
function ImageRow({
  images,
  gap = 8,
  onCheckBoxClick,
  selectedId = [],
  onClick = () => {},
  isAdmin,
  selectedTab,
  focusedIndex = 0,
  onTileFocus = () => {},
  registerPhotoTileRef = () => {},
}) {
  const eventFID = localStorage.getItem("eventFID");
  const { fid: folderId, id: eventId } = useParams();
  // const [errorImg, setErrorImg] = useState(false);
  const [loadingImg, setLoadingImg] = useState(true);
  const dispatch = useDispatch();

  const { sendRequest: thumbnailRequest, isLoading: isThumbnailRequest } =
    useApiHttp();
  const { sendRequest: downloadRequest } = useApiHttp();

  const downloadHande = (mediaId) => {
    downloadRequest(
      {
        url: `organizer/download`,
        method: "POST",
        base: "MEDIA",
        // formData: true,
        body: {
          mediaId,
          eventId,
        },
      },
      null,
      (data, res) => {
        const imageUrl = URL.createObjectURL(data);
        const fileName = getFileNameFromDisposition(res);
        downloadFile(imageUrl, fileName);
      },
      (err, data) => {
        Notifications.toast.error({
          id: "download-photos-error",
          title: data?.message,
        });
      },
    );
  };

  const thumbnailUpdate = (thumbnail) => {
    thumbnailRequest(
      {
        url: `event/update-thumbnail`,
        method: "PUT",
        body: {
          eventId,
          folderId: eventFID,
          thumbnail,
        },
        // formData: true,
      },
      null,
      (data) => {
        Notifications.toast.success({
          id: "upload-thumbnail",
          title: data?.message,
        });
      },
      (err, data) => {
        Notifications.toast.error({
          id: "upload-thumbnail",
          title: data?.message,
        });
      },
    );
  };

  return (
    <div className={`${classes["jv-row"]}`} style={{ gap, marginBottom: gap }}>
      {images.map((img) => {
        const isActive = selectedId.includes(img.id);
        return (
          <div
            key={img.thumbnailFileSignedPath}
            className={`${classes["jv-image-wrapper"]}`}
            style={{
              width: img.displayWidth,
              height: img.displayHeight,
            }}
            ref={(node) => registerPhotoTileRef(img.listIndex, node)}
            role="option"
            aria-selected={isActive}
            tabIndex={focusedIndex === img.listIndex ? 0 : -1}
            data-photo-index={img.listIndex}
            onFocus={() => onTileFocus(img.listIndex)}
            onClick={(event) => {
              if (selectedId.length > 0) {
                onCheckBoxClick(!isActive, img.listIndex, img.id, event);
              } else {
                onClick(img.listIndex, event);
              }
            }}
          >
            <img
              src={addParamsURL(decryptUrl(img.thumbnailFileSignedPath), {
                size: "s",
              })}
              alt={img.alt}
              loading="lazy"
              className={`${classes["jv-image"]}`}
              onError={(e) => {
                // setErrorImg(true);
                e.target.src = brokenImg;
              }}
            />

            {/* Hover Overlay */}
            <div
              className={`${classes["hover-overlay"]} ${isActive || selectedId.length > 0 ? classes["hover-overlay-active"] : ""} ${selectedId.length > 0 ? classes["check-box-active"] : ""}`}
            >
              <div className={`${classes["hover-header"]}`}>
                <div />
                {selectedTab !== "approvedPhotosAsPhotographer" && (
                  <div className={`${classes["header-options"]}`}>
                    <Box
                      className={`${classes["view-button"]}`}
                      sx={{
                        "--color-primary-grey-60": "white",
                      }}
                        title="View"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClick(img.listIndex, e);
                        }}
                    >
                      <Icon ic={"IcZoomIn"} color="grey-60" size="m" />
                    </Box>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={isActive}
                        onChange={(e) =>
                          onCheckBoxClick(
                            e.target.checked,
                            img.listIndex,
                            img.id,
                            e,
                          )
                        }
                        sx={{
                          color: "white",
                          "&.Mui-checked": {
                            color: "white",
                          },
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className={`${classes["bottom-bar"]}`}>
              <div className={`${classes["photo-details"]}`}>
                <span title={img.fileName}>{img.fileName}</span>
                <span title={img.userName}>~ {img.userName}</span>
              </div>
              <div className={`${classes["photo-actions"]}`}>
                {isAdmin && (
                  <Box
                    sx={{
                      "--color-primary-grey-60": "white",
                    }}
                    title="Download"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadHande(img.id);
                    }}
                  >
                    <Icon ic={"IcDownload"} color="grey-60" size="m" />
                  </Box>
                )}
                {isAdmin && selectedTab !== "photographersUploadedPhotos" && (
                  <Box
                    sx={{
                      "--color-primary-grey-60": "white",
                    }}
                    title="Set Thumbnail"
                    onClick={(e) => {
                      e.stopPropagation();
                      thumbnailUpdate(decryptUrl(img.reducedFileSignedPath));
                    }}
                  >
                    <Icon ic={"IcScanPicture"} color="grey-60" size="m" />
                  </Box>
                )}
                {isAdmin && (
                  <Box
                    sx={{
                      "--color-primary-grey-60": "white",
                    }}
                    title="Image info"
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(setFileDetails(img));
                      dispatch(openFileSideBar());
                    }}
                  >
                    <Icon ic={"IcInfo"} color="grey-60" size="m" />
                  </Box>
                )}
                {/* <Box
            sx={{
              "--color-primary-grey-60": "white",
            }}
          >
            <Icon ic={"IcTrash"} color="grey-60" size="m" />
          </Box> */}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ===============================
   MAIN COMPONENT
   =============================== */
export default function JustifiedVirtuosoGallery({
  images = [],
  rowHeight = 220,
  gap = 4,
  loading,
  endReached = () => {},
  selectedId = [],
  isAdmin = false,
  selectedTab,
  onCheckBoxChange = () => {},
  onClick = () => {},
  focusedIndex = 0,
  onTileFocus = () => {},
  registerPhotoTileRef = () => {},
}) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  // Observe container width
  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const ro = new ResizeObserver(([entry]) => {
      setWidth(entry.contentRect.width);
    });

    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const rows = useMemo(() => {
    if (!width || !images.length) return [];
    return buildJustifiedRows(images, width, rowHeight, gap);
  }, [width, images, rowHeight, gap]);
  return (
    <div
      ref={containerRef}
      style={{
        height: "100%",
        width: "100%",
        boxSizing: "border-box",
        padding: gap,
      }}
    >
      <Virtuoso
        totalCount={rows.length}
        className="custom-scrollbar"
        itemContent={(index) => (
          <ImageRow
            images={rows[index]}
            gap={gap}
            selectedId={selectedId}
            onClick={onClick}
            onCheckBoxClick={onCheckBoxChange}
            isAdmin={isAdmin}
            selectedTab={selectedTab}
            focusedIndex={focusedIndex}
            onTileFocus={onTileFocus}
            registerPhotoTileRef={registerPhotoTileRef}
          />
        )}
        components={{ Footer: loading ? Footer : "" }}
        endReached={endReached}
      />
    </div>
  );
}

const Footer = () => {
  return (
    <Loader size="small" containerProps={{ style: { marginTop: "16px" } }} />
  );
};
